// ABOUTME: Animated AI blob icon used in sidebar and chat header.
// ABOUTME: Static when idle, animates on hover. Use animate prop for continuous animation.

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"

interface AIIconProps {
  className?: string
  size?: number
  bgClassName?: string
  animate?: boolean
}

interface ControlPoint {
  angle: number
  baseRadius: number
  variation: number
  speed: number
  offset: number
}

const BLOB_CONFIG = {
  centerX: 200,
  centerY: 200,
  baseRadius: 100,
  complexity: 8,
}

function createControlPoints(): ControlPoint[] {
  const points: ControlPoint[] = []
  for (let i = 0; i < BLOB_CONFIG.complexity; i++) {
    const angle = (i / BLOB_CONFIG.complexity) * Math.PI * 2
    points.push({
      angle,
      baseRadius: BLOB_CONFIG.baseRadius,
      variation: Math.random() * 0.25 + 0.15,
      speed: Math.random() * 1.2 + 0.8,
      offset: Math.random() * Math.PI * 2,
    })
  }
  return points
}

function generateBlobPath(controlPoints: ControlPoint[], time: number): string {
  const points: { x: number; y: number; angle: number }[] = []

  for (const cp of controlPoints) {
    const radiusVariation =
      Math.sin(time * cp.speed + cp.offset) * cp.variation * cp.baseRadius * 0.8 +
      Math.sin(time * cp.speed * 2.1 + cp.offset * 1.5) * cp.variation * cp.baseRadius * 0.3

    const radius = cp.baseRadius + radiusVariation
    const x = BLOB_CONFIG.centerX + Math.cos(cp.angle) * radius
    const y = BLOB_CONFIG.centerY + Math.sin(cp.angle) * radius
    points.push({ x, y, angle: cp.angle })
  }

  const start = points[0]
  if (!start) return ""
  let path = `M ${start.x} ${start.y}`
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i]
    const p2 = points[(i + 1) % points.length]
    if (!p1 || !p2) continue
    const distance = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2) * 0.4

    const cp1x = p1.x + Math.cos(p1.angle + Math.PI / 2) * distance
    const cp1y = p1.y + Math.sin(p1.angle + Math.PI / 2) * distance
    const cp2x = p2.x + Math.cos(p2.angle - Math.PI / 2) * distance
    const cp2y = p2.y + Math.sin(p2.angle - Math.PI / 2) * distance

    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
  }
  return `${path} Z`
}

export const AIIcon: React.FC<AIIconProps> = ({
  className = "",
  size = 40,
  bgClassName = "bg-white shadow-lg",
  animate = false,
}) => {
  // useId wraps its token in `:` (React <19.1) or `«»` — stripped, since the
  // id is spliced into `url(#…)` attribute references below.
  const reactId = useId()
  const id = `ai-icon-${reactId.replace(/[^A-Za-z0-9_-]/g, "")}`
  const mainBlobRef = useRef<SVGPathElement>(null)
  const bgBlobRef = useRef<SVGPathElement>(null)
  const maskPathRef = useRef<SVGPathElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const highlight1Ref = useRef<SVGEllipseElement>(null)
  const highlight2Ref = useRef<SVGEllipseElement>(null)
  const glowEffectRef = useRef<HTMLDivElement>(null)

  // One random blob per mount, created once (a bare useRef(createControlPoints()) rolled and threw away a
  // new set on every render). The animation reads the points through the ref; the resting path is derived
  // from the same set below, so neither is read from a ref during render.
  // useMemo, not a one-element useState: react/hook-use-state wants state destructured with its setter, and
  // this value has none. An empty dependency list keeps it for the life of the icon.
  const initialControlPoints = useMemo(() => createControlPoints(), [])
  const controlPointsRef = useRef<ControlPoint[]>(initialControlPoints)
  const timeRef = useRef(0)
  const rotationRef = useRef(0)
  const animFrameRef = useRef<number | undefined>(undefined)

  const [isHovered, setIsHovered] = useState(false)
  const isAnimating = animate || isHovered

  // Generate static path on mount
  const staticPath = useMemo(() => generateBlobPath(initialControlPoints, 0), [initialControlPoints])

  // Set initial static path
  useEffect(() => {
    const path = staticPath
    mainBlobRef.current?.setAttribute("d", path)
    bgBlobRef.current?.setAttribute("d", path)
    maskPathRef.current?.setAttribute("d", path)
  }, [staticPath])

  const stopAnimation = useCallback(() => {
    if (animFrameRef.current !== undefined) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = undefined
    }
  }, [])

  // Animation loop — only runs while animating (hovered or animate prop)
  useEffect(() => {
    if (!isAnimating) {
      stopAnimation()

      // Reset to static state smoothly
      if (svgRef.current) {
        svgRef.current.style.transition = "transform 0.6s ease-out"
        svgRef.current.style.transform = "rotate(0deg)"
      }
      if (mainBlobRef.current) {
        mainBlobRef.current.style.transition = "transform 0.6s ease-out"
        mainBlobRef.current.style.transform = "scale(1)"
      }

      // Reset blob to static shape
      const path = staticPath
      mainBlobRef.current?.setAttribute("d", path)
      bgBlobRef.current?.setAttribute("d", path)
      maskPathRef.current?.setAttribute("d", path)

      // Reset highlights
      highlight1Ref.current?.setAttribute("cx", "140")
      highlight1Ref.current?.setAttribute("cy", "120")
      highlight2Ref.current?.setAttribute("cx", "260")
      highlight2Ref.current?.setAttribute("cy", "150")

      timeRef.current = 0
      rotationRef.current = 0
      return
    }

    // Clear transition so JS animation takes over
    if (svgRef.current) svgRef.current.style.transition = "none"
    if (mainBlobRef.current) mainBlobRef.current.style.transition = "none"

    let lastTime = Date.now()
    let pulsePhase = 0
    let glowPhase = 0
    let highlightPhase1 = 0
    let highlightPhase2 = 0
    let gradientTime = 0

    const animate = () => {
      const now = Date.now()
      const dt = (now - lastTime) / 1000
      lastTime = now

      // Blob morphing — faster on hover
      timeRef.current += isHovered ? 0.024 : 0.012
      const newPath = generateBlobPath(controlPointsRef.current, timeRef.current)
      mainBlobRef.current?.setAttribute("d", newPath)
      bgBlobRef.current?.setAttribute("d", newPath)
      maskPathRef.current?.setAttribute("d", newPath)

      // Rotation
      rotationRef.current += dt * 6
      if (svgRef.current) {
        svgRef.current.style.transform = `rotate(${rotationRef.current}deg)`
      }

      // Pulse
      pulsePhase += dt * 0.67
      const baseScale = isHovered ? 1.15 : 1
      const pulseScale = baseScale + Math.sin(pulsePhase * Math.PI * 2) * 0.02
      if (mainBlobRef.current) {
        mainBlobRef.current.style.transform = `scale(${pulseScale})`
        mainBlobRef.current.style.transformOrigin = "center"
      }

      // Glow
      glowPhase += dt * 0.8
      const baseGlowScale = isHovered ? 1.3 : 1
      const glowScale = baseGlowScale + Math.sin(glowPhase * Math.PI * 2) * 0.1
      const baseGlowOpacity = isHovered ? 0.8 : 0.5
      const glowOpacity = baseGlowOpacity + Math.sin(glowPhase * Math.PI * 2) * 0.1
      if (glowEffectRef.current) {
        glowEffectRef.current.style.transform = `translate(-50%, -50%) scale(${glowScale})`
        glowEffectRef.current.style.opacity = glowOpacity.toString()
      }

      // Highlights
      highlightPhase1 += dt * 0.5
      highlightPhase2 += dt * 0.57
      if (highlight1Ref.current) {
        highlight1Ref.current.setAttribute("cx", (140 + Math.sin(highlightPhase1 * Math.PI * 2) * 20).toString())
        highlight1Ref.current.setAttribute("cy", (120 + Math.cos(highlightPhase1 * Math.PI * 2) * -10).toString())
      }
      if (highlight2Ref.current) {
        highlight2Ref.current.setAttribute("cx", (260 + Math.sin(highlightPhase2 * Math.PI * 2) * -15).toString())
        highlight2Ref.current.setAttribute("cy", (150 + Math.cos(highlightPhase2 * Math.PI * 2) * 15).toString())
      }

      // Gradients
      gradientTime += 0.02
      const gradient2 = document.getElementById(`${id}-gradient2`)
      if (gradient2) {
        gradient2.setAttribute("cx", `${30 + Math.sin(gradientTime) * 10}%`)
        gradient2.setAttribute("cy", `${30 + Math.cos(gradientTime * 1.3) * 10}%`)
      }
      const gradient3 = document.getElementById(`${id}-gradient3`)
      if (gradient3) {
        gradient3.setAttribute("cx", `${70 + Math.cos(gradientTime * 0.7) * 10}%`)
        gradient3.setAttribute("cy", `${70 + Math.sin(gradientTime * 0.9) * 10}%`)
      }

      animFrameRef.current = requestAnimationFrame(animate)
    }

    animFrameRef.current = requestAnimationFrame(animate)
    return stopAnimation
  }, [isAnimating, isHovered, id, staticPath, stopAnimation])

  return (
    <div
      className={`relative ${className}`}
      style={{ width: size, height: size }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background circle */}
      <div className={`absolute inset-0 rounded-full ${bgClassName}`} />

      {/* Glow effect */}
      <div
        ref={glowEffectRef}
        className="absolute w-[70%] h-[70%] top-1/2 left-1/2 pointer-events-none mix-blend-screen transition-all duration-600 ease-out"
        style={{
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle at center, rgba(255, 255, 255, 0.5) 0%, rgba(255, 100, 100, 0.3) 30%, rgba(220, 20, 60, 0.3) 60%, transparent 100%)",
          filter: "blur(30px)",
          opacity: 0.5,
        }}
      />

      <svg
        ref={svgRef}
        viewBox="0 0 400 400"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 w-full h-full"
        style={{ transform: "translateZ(0)" }}
      >
        <defs>
          <linearGradient
            id={`${id}-gradient1`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop
              offset="0%"
              stopColor="#FF6B6B"
            />
            <stop
              offset="30%"
              stopColor="#FF4757"
            />
            <stop
              offset="70%"
              stopColor="#EE5A6F"
            />
            <stop
              offset="100%"
              stopColor="#C44569"
            />
          </linearGradient>

          <radialGradient
            id={`${id}-gradient2`}
            cx="30%"
            cy="30%"
          >
            <stop
              offset="0%"
              stopColor="#FFFFFF"
              stopOpacity="0.8"
            />
            <stop
              offset="40%"
              stopColor="#FFB3BA"
              stopOpacity="0.6"
            />
            <stop
              offset="100%"
              stopColor="#FF6B6B"
            />
          </radialGradient>

          <radialGradient
            id={`${id}-gradient3`}
            cx="70%"
            cy="70%"
          >
            <stop
              offset="0%"
              stopColor="#FFCCCC"
              stopOpacity="0.7"
            />
            <stop
              offset="50%"
              stopColor="#FF6B6B"
              stopOpacity="0.8"
            />
            <stop
              offset="100%"
              stopColor="#DC143C"
            />
          </radialGradient>

          <filter id={`${id}-glow`}>
            <feGaussianBlur
              stdDeviation="2"
              result="coloredBlur"
            />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter
            id={`${id}-blur`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="15"
            />
          </filter>

          <mask id={`${id}-blobMask`}>
            <path
              ref={maskPathRef}
              fill="white"
            />
          </mask>
        </defs>

        {/* Blurred background */}
        <g
          filter={`url(#${id}-blur)`}
          opacity="0.4"
        >
          <path
            ref={bgBlobRef}
            fill={`url(#${id}-gradient1)`}
          />
        </g>

        {/* Main shape with mask */}
        <g mask={`url(#${id}-blobMask)`}>
          <rect
            x="0"
            y="0"
            width="400"
            height="400"
            fill={`url(#${id}-gradient1)`}
          />
          <ellipse
            cx="120"
            cy="120"
            rx="100"
            ry="100"
            fill={`url(#${id}-gradient2)`}
            opacity="0.8"
          />
          <ellipse
            cx="280"
            cy="280"
            rx="120"
            ry="120"
            fill={`url(#${id}-gradient3)`}
            opacity="0.7"
          />
        </g>

        {/* Main blob path */}
        <path
          ref={mainBlobRef}
          fill={`url(#${id}-gradient1)`}
          filter={`url(#${id}-glow)`}
          style={{ opacity: 0.95, transform: "translateZ(0)" }}
        />

        {/* Highlights */}
        <ellipse
          ref={highlight1Ref}
          cx="140"
          cy="120"
          rx="40"
          ry="30"
          fill="white"
          opacity="0.3"
          filter={`url(#${id}-blur)`}
        />
        <ellipse
          ref={highlight2Ref}
          cx="260"
          cy="150"
          rx="30"
          ry="25"
          fill="white"
          opacity="0.2"
          filter={`url(#${id}-blur)`}
        />
      </svg>
    </div>
  )
}
